import { useMemo } from 'react';
import { usePlan } from '../store/planStore.js';
import { INGREDIENTS_BY_ID } from '../../data/index.js';
import { householdPortions } from '../../domain/portions.js';
import { staplesNeeded } from './cupboard.js';
import { useMeals } from '../recipes/useMeals.js';

export function CupboardCheck({ onDone }: { onDone: () => void }): React.JSX.Element {
  const { ready, plan, settings, setPantry } = usePlan();
  const catalogue = useMeals();

  // A household of zero is a legitimate resting state in Settings (each `−`
  // clamps at 0), but it makes every dinner ingredient's requirement collapse
  // to zero too. Asking "got olive oil already?" for a plan that will buy
  // nothing is worse than useless — it hides the real problem behind a normal-
  // looking screen. Catch it here, before any requirement is even computed.
  const household = ready ? householdPortions(settings.people) : null;

  const needed = useMemo(() => {
    if (!ready || household === 0) return [];
    return staplesNeeded(plan, settings.people, catalogue);
  }, [ready, household, plan, settings.people, catalogue]);

  if (!ready) return <p>Loading</p>;

  if (household === 0) {
    return (
      <section className="cupboard">
        <p className="empty-note">
          Nobody is set to eat, so every meal would buy nothing. Set portion factors
          in Settings.
        </p>
        <button type="button" className="primary" onClick={onDone}>Build the list</button>
      </section>
    );
  }

  const have = needed.filter((id) => plan.pantry[id] === true).length;

  return (
    <section className="cupboard">
      <header className="plan-head">
        <h1>Got these already?</h1>
        <p className="plan-count">
          {needed.length === 0
            ? 'Nothing to check — this week needs no cupboard staples.'
            : `${have} already in, ${needed.length - have} to buy`}
        </p>
      </header>

      <ul className="staple-list">
        {needed.map((id) => (
          <li key={id}>
            <button
              type="button"
              aria-pressed={plan.pantry[id] === true}
              onClick={() => setPantry(id, plan.pantry[id] !== true)}
            >
              <span className="tickbox" aria-hidden="true" />
              {INGREDIENTS_BY_ID.get(id)?.name ?? id}
            </button>
          </li>
        ))}
      </ul>

      <button type="button" className="primary" onClick={onDone}>Build the list</button>
    </section>
  );
}
